import { GridLogicOperator, GridFilterModel as MuiGridFilterModel, GridSortModel as MuiGridSortModel } from '@mui/x-data-grid'
import { AssertNeverError, AvailableLocale } from '@omnea-digital/toolbox'
import { GridFilterItem, GridFilterModel, GridFilterItemSchema } from '@omnea-app/packages/universal/tables/gridModel'
import { ROW_GROUPING_SINGLE_GROUPING_FIELD } from '../row-grouping/helpers'

interface TransformFilterModelOptions {
  filterModel?: MuiGridFilterModel
  filterNullValues?: boolean
  locale?: AvailableLocale
}
interface TransformSortModelOptions {
  sortModel?: MuiGridSortModel
  // The active grouping fields, when the grid is grouped. Used to resolve MUI's grouping pseudo-field.
  groupFields?: readonly string[]
}

// Transforms MUI's data grid model into our own model
// This function includes safe parsing so we have expected (operator, valueTypes) pairs for backend filtering
export function transformMuiFilterModelToOmnea({
  filterModel,
  filterNullValues = false,
  locale,
}: TransformFilterModelOptions): GridFilterModel | undefined {
  if (filterModel == null) {
    return undefined
  }

  const items = filterModel.items
  if (items.length === 0 && !filterModel.quickFilterValues) {
    return undefined
  }

  const processedItems = filterNullValues
    ? items.filter(
        (item) =>
          item.operator === 'isEmpty' || item.operator === 'isNotEmpty' || (item.value !== null && item.value !== undefined)
      )
    : items

  const transformedItems = processedItems.flatMap((item) => {
    const parsed = GridFilterItemSchema.safeParse(item)

    if (!parsed.success) {
      console.error('Failed to parse filter item:', parsed.error)
      return []
    }

    return [parsed.data]
  })

  if (transformedItems.length === 0 && !filterModel.quickFilterValues) {
    return undefined
  }

  const enrichedItems = locale ? transformedItems.map((item) => enrichFilterItemWithLocale(item, locale)) : transformedItems

  const logicOperator = filterModel?.logicOperator
  return {
    items: enrichedItems,
    ...(logicOperator === GridLogicOperator.And
      ? { logicOperator: 'and' }
      : logicOperator === GridLogicOperator.Or
        ? { logicOperator: 'or' }
        : {}),
    quickFilterValues: filterModel?.quickFilterValues,
  }
}

const enrichFilterItemWithLocale = (item: GridFilterItem, locale: AvailableLocale): GridFilterItem => {
  if (item.field === 'address' && item.operator === 'contains') {
    return { ...item, context: { locale } }
  }
  return item
}

export function transformMuiSortModelToOmnea({ sortModel, groupFields }: TransformSortModelOptions) {
  if (!sortModel || sortModel.length === 0) {
    return undefined
  }

  // A single-level grouping field the pseudo-field can be mapped onto; more than one is not server-served.
  const groupField = groupFields?.length === 1 ? groupFields[0] : undefined

  // Sorting by the group-column header sends MUI's grouping pseudo-field. Map it to the grouping field so
  // the server keeps the intended group ordering; strip it when there is no single grouping field to map
  // onto, since forwarding the pseudo-field would be an unknown column server-side.
  return sortModel
    .filter((item) => item.sort !== null)
    .flatMap((item) => {
      const field = item.field === ROW_GROUPING_SINGLE_GROUPING_FIELD ? groupField : item.field
      if (field === undefined) return []
      return [{ field, sort: item.sort as 'asc' | 'desc' }]
    })
}

export function transformOmneaFilterModelToMui(filterModel?: GridFilterModel): MuiGridFilterModel {
  if (!filterModel) {
    return { items: [] }
  }

  const { items, logicOperator, quickFilterValues } = filterModel

  return {
    items,
    ...(logicOperator ? { logicOperator: convertLogicOperatorToMui(logicOperator) } : {}),
    quickFilterValues,
  }
}

const convertLogicOperatorToMui = (logicOperator: 'and' | 'or'): GridLogicOperator => {
  if (logicOperator === 'and') {
    return GridLogicOperator.And
  }
  if (logicOperator === 'or') {
    return GridLogicOperator.Or
  }
  throw new AssertNeverError(logicOperator)
}
